/**
 * Signal Administration (Izba Admin) — shapes returned by
 * /api/v1/admin/signal-administration/*. Backend:
 * routers/signal_administration.py, services/signal_admin_config_service.py,
 * utils/signal_catalog.py.
 */

export type ServiceMode = "active" | "inactive";
export type ActionsSurface = "enabled" | "disabled" | "hidden";
/** Which organizations get signals: every one, or only those an Izba admin
 * turned on (their override sets the Signal Service Active). */
export type OrganizationsScope = "all" | "opted_in";
export type SignalTier = "critical" | "warning" | "opp" | "maint";

export interface SignalAdminOrganization {
  org_id: string;
  org_name: string;
  domain: string;
  is_active: boolean;
}

export interface RuleControls {
  evaluation: boolean;
  delivery: boolean;
}

export interface SignalAdminConfig {
  schema_version: number;
  service_mode: ServiceMode;
  actions_surface: ActionsSurface;
  /** Platform only; absent (an older API) = every organization. */
  organizations?: OrganizationsScope;
  layers: Record<string, boolean>;
  rules: Record<string, RuleControls>;
  /** Izba-admin overrides only; absent = default / vendor value. */
  parameters: Record<string, number>;
  /** Tier each signal fires at; absent = the ExecBridge default (with its
   * automatic escalation, where the rule has one). */
  tiers?: Record<string, SignalTier>;
  /** Clusters each rule fires for ({scope id: cluster keys}); absent = the
   * rule's default clusters. The cluster cut-offs belong to My Accounts. */
  rule_clusters?: Record<string, string[]>;
  /** Izba-admin CTA overrides only; absent = ExecBridge default. */
  ctas: Record<string, CtaOverride>;
  /** Target Registry changes only; absent = the imported seed. */
  targets?: Record<string, TargetPatch>;
  /** Action Guides by definition id; absent = the CTA just navigates. */
  guides?: Record<string, ActionGuide>;
  /** Card text overrides by definition id; absent = default text. */
  card_copy?: Record<string, CardCopy>;
  /** Registry review items an admin closed: {definition_id: note}. */
  review_resolved?: Record<string, string>;
  /** Reviewed signal action plans; absent = ExecBridge's proposal. */
  action_plans?: Record<string, ActionPlan>;
  /** Rule Classifier rules an admin changed or added, by rule id; absent =
   * the rule as shipped in backend/utils/signal_rules.json. */
  classifier?: Record<string, ClassifierRuleRecord>;
  /** Signal pipeline timing; absent = the server's defaults. */
  pipeline?: PipelineSettings;
}

export interface PipelineSettings {
  sweep_interval_minutes?: number;
  recheck_interval_hours?: number;
}

/** GET pipeline-status: intervals in force plus this API process's timing. */
export interface PipelineStatus {
  sweep_interval_minutes: number;
  sweep_source: "admin" | "server_default";
  recheck_interval_hours: number;
  recheck_source: "admin" | "server_default";
  server_default_sweep_minutes: number;
  server_default_recheck_hours: number;
  bounds: Record<keyof PipelineSettings, [number, number]>;
  sweeper_running: boolean;
  sweep_in_progress: boolean;
  last_sweep_started: string | null;
  last_sweep_ended: string | null;
  last_sweep_trigger: "schedule" | "manual" | null;
  last_sweep_totals: Record<string, number> | null;
  next_sweep_due: string | null;
  recheck_running: boolean;
  last_recheck: string | null;
  next_recheck_due: string | null;
}

/** Who takes a sub-action. "you" is the card's own user. */
export type ActionOwner =
  | "you"
  | "exec_sponsor"
  | "account_team"
  | "customer"
  | "approver";

/** Derived from the steps: one step = single; any step not owned by
 * "you" = dependent. */
export type ActionBucket =
  | "single_independent"
  | "single_dependent"
  | "multiple_independent"
  | "multiple_dependent";

export interface ActionPlanStep {
  title: string;
  owner: ActionOwner;
  /** Target Registry id, or "" when the step happens outside ExecBridge. */
  target: string;
  detail: string;
}

/** A reviewed plan as stored (backend _clean_action_plan). */
export interface ActionPlan {
  steps: ActionPlanStep[];
  rationale: string;
  status: "approved" | "changes_requested";
  note: string;
  /** One review action; acting doesn't clear it, data changes do. */
  awareness?: boolean;
}

export type ActionPlanStatus = "proposed" | ActionPlan["status"];

export interface EffectiveActionPlan {
  steps: (ActionPlanStep & {
    owner_label: string;
    target_label: string | null;
    target_available: boolean;
  })[];
  rationale: string;
  status: ActionPlanStatus;
  note: string;
  awareness: boolean;
  awareness_disclaimer: string | null;
  bucket: ActionBucket;
  bucket_label: string;
  is_default: boolean;
  matches_default: boolean;
}

/** Overridden card text; a missing field keeps the default. Supports the
 * same {{field}} placeholders as the default text. */
export interface CardCopy {
  title?: string;
  text?: string;
  why?: string;
}

/** An Action Guide as stored (backend _clean_guide). `live` guides open
 * from the card's CTA; others are saved but not shown. */
export interface ActionGuide {
  live: boolean;
  title: string;
  why: string;
  steps: { title: string; body: string; target: string }[];
  clears_when: string;
}

export type TargetSurface = "page" | "tab" | "unclassified";
export type TargetReviewStatus = "needs_review" | "confirmed" | "rejected";

/** What an Izba admin can change on a target (backend TARGET_FIELDS). */
export interface TargetPatch {
  label?: string;
  description?: string;
  surface_type?: TargetSurface;
  parent_page?: string;
  route?: string;
  personas?: string[];
  permission?: string;
  required_context?: string;
  review_status?: TargetReviewStatus;
  enabled?: boolean;
  fallback_target?: string;
}

/** One Target Registry entry as the backend resolves it. */
export interface TargetRecord {
  id: string;
  label: string;
  description: string;
  surface_type: TargetSurface;
  parent_page: string;
  /** In-app path; {account_id} is filled per card. Empty for the
   * assessment flow and for targets with no route. */
  route: string;
  route_label: string;
  /** The mock's intended route for an imported legacy target. */
  legacy_route: string;
  kind: RouteKind | null;
  provenance: string;
  personas: string[];
  permission: string;
  required_context: string;
  review_status: TargetReviewStatus;
  legacy_active: boolean;
  enabled: boolean;
  fallback_target: string;
  /** Imported from the catalogue (route is code-backed, not editable). */
  seeded: boolean;
  available: boolean;
  accepts_new_mappings: boolean;
  /** Canonical definitions whose CTA maps here under the applied config. */
  mappings: string[];
}

export interface CtaOverride {
  label: string;
  target: string;
  destination: string;
  permission: string;
  resolution: string;
}

export type RouteKind =
  | "assess"
  | "relationships"
  | "synopsis"
  | "path"
  | "brief";

export interface EffectiveCta {
  label: string;
  target: string | null;
  kind: RouteKind;
  path: string | null;
  destination: string | null;
  permission: string | null;
  resolution: string | null;
  source: "override" | "default";
  mapped_target: string | null;
  effective_target: string | null;
  target_available: boolean;
  target_reason: string | null;
}

export interface SignalAdminPresentation {
  service_mode: ServiceMode;
  actions_surface: ActionsSurface;
  effective_actions_surface: ActionsSurface;
  signals_visible: boolean;
  history_available: boolean;
  organizations?: OrganizationsScope;
  config_version: number;
}

export interface SignalAdminConfigResponse {
  organization: SignalAdminOrganization;
  config: SignalAdminConfig;
  presentation: SignalAdminPresentation;
  version: number;
  applied_at: string | null;
  applied_by_email: string | null;
}

/** Per-organization Signal Service / Open Actions (platform baseline + override). */
export interface SignalAdminOrgPresentation {
  organization: SignalAdminOrganization;
  platform: {
    service_mode: ServiceMode;
    actions_surface: ActionsSurface;
    organizations?: OrganizationsScope;
    version: number;
    applied_at: string | null;
  };
  override: {
    version: number;
    service_mode: ServiceMode | null;
    actions_surface: ActionsSurface | null;
    changes: SignalAdminChange[];
    reason: string;
    applied_by_email: string | null;
    applied_at: string;
  } | null;
  has_override: boolean;
  /** This organization's override turns signals on (Signal Service Active).
   * Under "Only organizations turned on" that alone decides whether its
   * users see any. */
  turned_on?: boolean;
  config: {
    service_mode: ServiceMode;
    actions_surface: ActionsSurface;
  };
  presentation: SignalAdminPresentation;
  version: number;
  applied_at: string | null;
  applied_by_email: string | null;
  scope: "organization";
}

export interface OrgPresentationDraft {
  service_mode: ServiceMode;
  actions_surface: ActionsSurface;
}

export interface SignalAdminChange {
  field: string;
  label: string;
  from: string | number;
  to: string | number;
}

export interface SignalAdminVersion {
  version: number;
  config: SignalAdminConfig;
  changes: SignalAdminChange[];
  reason: string;
  applied_by_user_id: string | null;
  applied_by_email: string | null;
  applied_at: string;
}

export interface SignalAdminOverview {
  open_signals: number;
  open_signals_by_tier: Partial<Record<SignalTier, number>>;
  accounts_with_open_signals: number;
  total_accounts: number;
  users_with_open_signals: number;
  last_evaluated_at: string | null;
}

export type RuntimeState =
  | "active"
  | "active_not_delivered"
  | "skipped"
  | "blocked";

export interface RuleState {
  evaluation: boolean;
  delivery: boolean;
  evaluates: boolean;
  delivers: boolean;
  runtime: RuntimeState;
  reasons: string[];
  blocked_layers: string[];
}

export interface DynamicTier {
  escalated: SignalTier;
  /** May hold {{parameter-id}} placeholders. */
  when: string;
}

export interface SignalDefinition {
  id: string;
  key: string;
  variant: string | null;
  order: string;
  tier: SignalTier;
  title: string;
  trigger: string;
  family: string;
  type: "base" | "derived" | "status";
  layers: string[];
  target: string;
  cta: string;
  destination: string;
  scope: string;
  personas: string[];
  provenance: string;
  definition_status: "canonical" | "proposed";
  depends: string[];
  fallback: string | null;
  review: string;
  review_note: string | null;
  resolution: string;
  implementation_status:
    | "implemented"
    | "canonical_unimplemented"
    | "proposed_unimplemented";
  state: RuleState;
  /** Null on the platform registry (no organization chosen). */
  open_firings: number | null;
  used_by: string[];
  parameters: string[];
  /** Cluster choices (ids into SignalAdminRegistry.cluster_scopes). */
  cluster_scopes: string[];
  /** `tier` is the ExecBridge default; this is the applied admin choice. */
  tier_override?: SignalTier | null;
  /** Set when the default tier escalates on its own. */
  dynamic_tier?: DynamicTier | null;
  cta_effective: EffectiveCta;
  action_plan: EffectiveActionPlan | null;
  /** {{field}} placeholders this signal's text may use. */
  placeholders: string[];
  /** Of those, the ones that can be empty when it fires (render as "Unknown"). */
  nullable_placeholders: string[];
  cta_default: { label: string; kind: RouteKind; destination: string };
}

export interface DataLayer {
  id: string;
  label: string;
  source: string;
  legacy: string;
  expected_cadence: string;
  record_age_policy: string;
  enabled: boolean;
  dependent_definitions: string[];
  dependent_implemented: number;
}

export interface SignalParameter {
  id: string;
  label: string;
  default: number;
  unit: string;
  operator: string;
  signals: string[];
  range: [number, number];
  step: number;
  field: string;
  applies: "evaluation" | "public_data";
  vendor_value: number | null;
  override: number | null;
  effective: number;
}

/** Which Account Health clusters a rule fires for, chosen in Signal
 * Administration; the clusters' score cut-offs stay with My Accounts. */
export interface ClusterScope {
  id: string;
  label: string;
  field: string;
  default: string[];
  signals: string[];
  note: string;
  override: string[] | null;
  effective: string[];
}

export interface SignalAdminRegistry {
  version: number;
  families: Record<string, string>;
  targets: Record<string, string>;
  target_registry: TargetRecord[];
  /** ExecBridge default guides — live wherever no admin guide is stored
   * (a stored guide, live or switched off, wins). */
  guide_suggestions: Record<string, Omit<ActionGuide, "live">>;
  layers: DataLayer[];
  definitions: SignalDefinition[];
  parameters: SignalParameter[];
  cluster_scopes: ClusterScope[];
  /** Cluster key → label, in cluster order (Triage … Expand). */
  cluster_labels: Record<string, string>;
  route_kinds: Record<string, RouteKind>;
  route_kind_labels: Record<RouteKind, string>;
  action_plan_owners: Record<ActionOwner, string>;
  action_plan_buckets: Record<ActionBucket, { label: string; hint: string }>;
  /** ExecBridge's proposed plans, keyed by definition id. */
  action_plan_defaults: Record<
    string,
    { steps: ActionPlanStep[]; rationale: string; awareness?: boolean }
  >;
  action_plan_awareness_disclaimer: string;
  classifier: ClassifierView;
}

/** Rule Classifier types: rules only hide. Combining cards isn't a rule —
 * users get one card per account (Same CTA and Combine retired 2026-10-23). */
export type ClassifierRuleType = "duplicate" | "supersede";
export type ClassifierRuleStatus = "proposed" | "active" | "off";

/** One rule as stored: signal ids are catalogue definition ids. */
export interface ClassifierRuleRecord {
  type: ClassifierRuleType;
  lead: string;
  members: string[];
  status: ClassifierRuleStatus;
  note: string;
}

export interface ClassifierRule extends ClassifierRuleRecord {
  id: string;
  /** code = as shipped; changed / added = an admin change not in code. */
  source: "code" | "changed" | "added";
  /** The shipped copy (null for an admin-added rule), for Reset. */
  code: (ClassifierRuleRecord & { id: string }) | null;
  /** What stops it being switched on now. */
  errors: string[];
}

export interface ClassifierView {
  rules: ClassifierRule[];
  types: Record<ClassifierRuleType, string>;
  statuses: Record<ClassifierRuleStatus, string>;
  /** Applied admin changes that Export to code would carry. */
  not_in_code: number;
  /** signal_rules.json as it would be with the applied changes. */
  export: {
    schema_version: number;
    rules: (ClassifierRuleRecord & { id: string })[];
  };
}

/** Rule Classifier impact in one organization (each rule judged as if
 * switched on): accounts it touches today and cards it hides. */
export interface ClassifierImpact {
  rules: Record<
    string,
    {
      accounts: number;
      cards: number;
      examples: { id: string; name: string }[];
    }
  >;
  accounts_with_signals: number;
  accounts: { id: string; name: string; open: number }[];
}

export interface ClassifierTryCard {
  definition_id: string;
  label: string;
  tier: SignalTier;
  cta: {
    label: string | null;
    kind: string | null;
    destination: string | null;
  };
  family?: string | null;
}

/** One account's cards now and under the draft rules. */
export interface ClassifierTry {
  account: { id: string; name: string | null };
  before: ClassifierTryCard[];
  after: (ClassifierTryCard & {
    outcome: "shown" | "covered";
    rule?: string;
    by?: string;
  })[];
  not_delivered: ClassifierTryCard[];
}

export type TraceResult =
  | "delivered"
  | "hidden"
  | "not_delivered"
  | "skipped"
  | "blocked";

export interface SignalTrace {
  run_id: string;
  account_id: string;
  account_name: string;
  definition_id: string;
  title: string;
  signal_key: string;
  tier: SignalTier | null;
  result: TraceResult;
  reason: string | null;
  config_version: number;
  triggered_by: string | null;
  triggered_fields: Record<string, unknown> | null;
  evaluated_at: string;
}

export interface HistoryEvent {
  event: string;
  at: string;
  actor: string | null;
  persona: string | null;
  cta_label?: string | null;
  reason?: string | null;
  /** Stamped on CTA clicks: what was live when the user clicked. */
  guide?: string | null;
  card_text?: "default" | "edited" | null;
  card_title?: string | null;
  config_version?: number | null;
  /** On guide_step events: the step's title and whether it was ticked. */
  guide_step?: string | null;
  done?: boolean | null;
}

export interface HistoryRecord {
  firing_id: string;
  account_id: string;
  account_name: string;
  signal_key: string;
  title: string;
  tier: SignalTier;
  state: string;
  resolution_reason: string | null;
  rule_version: string | null;
  events: HistoryEvent[];
  personas: string[];
}

/** Everything an Izba admin can stage before applying. */
export interface SignalAdminDraft {
  service_mode: ServiceMode;
  actions_surface: ActionsSurface;
  organizations?: OrganizationsScope;
  layers: Record<string, boolean>;
  rules: Record<string, RuleControls>;
  parameters: Record<string, number>;
  tiers: Record<string, SignalTier>;
  rule_clusters: Record<string, string[]>;
  ctas: Record<string, CtaOverride>;
  guides: Record<string, ActionGuide>;
  card_copy: Record<string, CardCopy>;
  /** null = remove an admin-authored target. */
  targets: Record<string, TargetPatch | null>;
  review_resolved: Record<string, string>;
  action_plans: Record<string, ActionPlan>;
  pipeline: PipelineSettings;
  /** Rule Classifier admin copies; a rule left out uses the code file. */
  classifier: Record<string, ClassifierRuleRecord>;
}

/** Parameter values a preview should show for this draft: the draft's
 * value, else the selected org's own setting, else the default — the same
 * order the evaluator uses. Fills {{parameter-id}} in card and guide text. */
export const draftParameterValues = (
  registry:
    | Pick<
        SignalAdminRegistry,
        "parameters" | "cluster_scopes" | "cluster_labels"
      >
    | null
    | undefined,
  draft:
    | Pick<SignalAdminDraft, "parameters" | "rule_clusters">
    | null
    | undefined
): Record<string, number | string> => ({
  ...Object.fromEntries(
    (registry?.parameters ?? []).map(p => [
      p.id,
      draft?.parameters?.[p.id] ?? p.vendor_value ?? p.default,
    ])
  ),
  // Cluster choices read as words, as the feed sends them: "Grow or Expand".
  ...Object.fromEntries(
    (registry?.cluster_scopes ?? []).map(scope => [
      scope.id,
      clusterText(
        draft?.rule_clusters?.[scope.id] ?? scope.default,
        registry?.cluster_labels ?? {}
      ),
    ])
  ),
});

/** "Triage", "Grow or Expand" — mirrors the backend's cluster_text. */
export const clusterText = (
  clusters: string[],
  labels: Record<string, string>
): string => {
  const words = Object.keys(labels)
    .filter(key => clusters.includes(key))
    .map(key => labels[key]);
  return words.length ? words.join(" or ") : "no cluster";
};

export const draftFromConfig = (
  config: SignalAdminConfig
): SignalAdminDraft => ({
  service_mode: config.service_mode,
  actions_surface: config.actions_surface,
  organizations: config.organizations ?? "all",
  layers: { ...config.layers },
  rules: Object.fromEntries(
    Object.entries(config.rules).map(([id, c]) => [id, { ...c }])
  ),
  parameters: { ...config.parameters },
  tiers: { ...(config.tiers ?? {}) },
  rule_clusters: Object.fromEntries(
    Object.entries(config.rule_clusters ?? {}).map(([id, c]) => [id, [...c]])
  ),
  ctas: Object.fromEntries(
    Object.entries(config.ctas ?? {}).map(([id, c]) => [id, { ...c }])
  ),
  targets: Object.fromEntries(
    Object.entries(config.targets ?? {}).map(([id, t]) => [
      id,
      { ...t, personas: t.personas ? [...t.personas] : undefined },
    ])
  ),
  guides: Object.fromEntries(
    Object.entries(config.guides ?? {}).map(([id, g]) => [
      id,
      { ...g, steps: g.steps.map(step => ({ ...step })) },
    ])
  ),
  card_copy: Object.fromEntries(
    Object.entries(config.card_copy ?? {}).map(([id, c]) => [id, { ...c }])
  ),
  review_resolved: { ...(config.review_resolved ?? {}) },
  action_plans: Object.fromEntries(
    Object.entries(config.action_plans ?? {}).map(([id, p]) => [
      id,
      { ...p, steps: p.steps.map(step => ({ ...step })) },
    ])
  ),
  pipeline: { ...(config.pipeline ?? {}) },
  classifier: Object.fromEntries(
    Object.entries(config.classifier ?? {}).map(([id, r]) => [
      id,
      { ...r, members: [...r.members] },
    ])
  ),
});

export interface DraftPayload {
  service_mode?: ServiceMode;
  actions_surface?: ActionsSurface;
  organizations?: OrganizationsScope;
  layers?: Record<string, boolean>;
  rules?: Record<string, Partial<RuleControls>>;
  parameters?: Record<string, number | null>;
  tiers?: Record<string, SignalTier | null>;
  rule_clusters?: Record<string, string[] | null>;
  ctas?: Record<string, CtaOverride | null>;
  targets?: Record<string, TargetPatch | null>;
  guides?: Record<string, ActionGuide | null>;
  card_copy?: Record<string, CardCopy | null>;
  review_resolved?: Record<string, string | null>;
  action_plans?: Record<string, ActionPlan | null>;
  pipeline?: Record<string, number | null>;
  classifier?: Record<string, ClassifierRuleRecord | null>;
}

/** The minimal apply payload for draft vs applied config, and how many
 * individual settings it changes. */
export const draftPayload = (
  config: SignalAdminConfig,
  draft: SignalAdminDraft
): { payload: DraftPayload; count: number } => {
  const payload: DraftPayload = {};
  let count = 0;
  if (draft.service_mode !== config.service_mode) {
    payload.service_mode = draft.service_mode;
    count += 1;
  }
  if (draft.actions_surface !== config.actions_surface) {
    payload.actions_surface = draft.actions_surface;
    count += 1;
  }
  if (
    draft.organizations !== undefined &&
    draft.organizations !== (config.organizations ?? "all")
  ) {
    payload.organizations = draft.organizations;
    count += 1;
  }
  Object.entries(draft.layers).forEach(([id, enabled]) => {
    if (config.layers[id] !== enabled) {
      payload.layers = { ...payload.layers, [id]: enabled };
      count += 1;
    }
  });
  Object.entries(draft.rules).forEach(([id, controls]) => {
    const applied = config.rules[id];
    (["evaluation", "delivery"] as const).forEach(control => {
      if (applied && applied[control] !== controls[control]) {
        payload.rules = {
          ...payload.rules,
          [id]: { ...payload.rules?.[id], [control]: controls[control] },
        };
        count += 1;
      }
    });
  });
  const paramIds = new Set([
    ...Object.keys(config.parameters),
    ...Object.keys(draft.parameters),
  ]);
  paramIds.forEach(id => {
    const before = config.parameters[id];
    const after = draft.parameters[id];
    if (before !== after) {
      payload.parameters = {
        ...payload.parameters,
        [id]: after === undefined ? null : after,
      };
      count += 1;
    }
  });
  const tierIds = new Set([
    ...Object.keys(config.tiers ?? {}),
    ...Object.keys(draft.tiers ?? {}),
  ]);
  tierIds.forEach(id => {
    const before = config.tiers?.[id];
    const after = draft.tiers?.[id];
    if ((before ?? null) !== (after ?? null)) {
      payload.tiers = { ...payload.tiers, [id]: after ?? null };
      count += 1;
    }
  });
  const clusterIds = new Set([
    ...Object.keys(config.rule_clusters ?? {}),
    ...Object.keys(draft.rule_clusters ?? {}),
  ]);
  clusterIds.forEach(id => {
    const before = config.rule_clusters?.[id];
    const after = draft.rule_clusters?.[id];
    if (JSON.stringify(before ?? null) !== JSON.stringify(after ?? null)) {
      payload.rule_clusters = { ...payload.rule_clusters, [id]: after ?? null };
      count += 1;
    }
  });
  const ctaIds = new Set([
    ...Object.keys(config.ctas ?? {}),
    ...Object.keys(draft.ctas),
  ]);
  ctaIds.forEach(id => {
    const before = config.ctas?.[id];
    const after = draft.ctas[id];
    if (JSON.stringify(before ?? null) !== JSON.stringify(after ?? null)) {
      payload.ctas = { ...payload.ctas, [id]: after ?? null };
      count += 1;
    }
  });
  const targetIds = new Set([
    ...Object.keys(config.targets ?? {}),
    ...Object.keys(draft.targets),
  ]);
  targetIds.forEach(id => {
    const before = config.targets?.[id];
    const after = draft.targets[id];
    if (JSON.stringify(before ?? null) !== JSON.stringify(after ?? null)) {
      payload.targets = { ...payload.targets, [id]: after ?? null };
      count += 1;
    }
  });
  const guideIds = new Set([
    ...Object.keys(config.guides ?? {}),
    ...Object.keys(draft.guides),
  ]);
  guideIds.forEach(id => {
    const before = config.guides?.[id];
    const after = draft.guides[id];
    if (JSON.stringify(before ?? null) !== JSON.stringify(after ?? null)) {
      payload.guides = { ...payload.guides, [id]: after ?? null };
      count += 1;
    }
  });
  const copyIds = new Set([
    ...Object.keys(config.card_copy ?? {}),
    ...Object.keys(draft.card_copy),
  ]);
  copyIds.forEach(id => {
    const before = config.card_copy?.[id];
    const after = draft.card_copy[id];
    if (JSON.stringify(before ?? null) !== JSON.stringify(after ?? null)) {
      payload.card_copy = { ...payload.card_copy, [id]: after ?? null };
      count += 1;
    }
  });
  const reviewIds = new Set([
    ...Object.keys(config.review_resolved ?? {}),
    ...Object.keys(draft.review_resolved),
  ]);
  reviewIds.forEach(id => {
    const before = config.review_resolved?.[id];
    const after = draft.review_resolved[id];
    if ((before ?? null) !== (after ?? null)) {
      payload.review_resolved = {
        ...payload.review_resolved,
        [id]: after ?? null,
      };
      count += 1;
    }
  });
  const planIds = new Set([
    ...Object.keys(config.action_plans ?? {}),
    ...Object.keys(draft.action_plans ?? {}),
  ]);
  planIds.forEach(id => {
    const before = config.action_plans?.[id];
    const after = draft.action_plans?.[id];
    if (JSON.stringify(before ?? null) !== JSON.stringify(after ?? null)) {
      payload.action_plans = { ...payload.action_plans, [id]: after ?? null };
      count += 1;
    }
  });
  const classifierIds = new Set([
    ...Object.keys(config.classifier ?? {}),
    ...Object.keys(draft.classifier ?? {}),
  ]);
  classifierIds.forEach(id => {
    const before = config.classifier?.[id] ?? null;
    const after = draft.classifier?.[id] ?? null;
    if (JSON.stringify(before) !== JSON.stringify(after)) {
      payload.classifier = { ...payload.classifier, [id]: after };
      count += 1;
    }
  });
  (["sweep_interval_minutes", "recheck_interval_hours"] as const).forEach(
    field => {
      const before = config.pipeline?.[field];
      const after = draft.pipeline?.[field];
      if ((before ?? null) !== (after ?? null)) {
        payload.pipeline = { ...payload.pipeline, [field]: after ?? null };
        count += 1;
      }
    }
  );
  return { payload, count };
};

/** Replay `draft`'s own changes (relative to `base`, the config it was
 * made against) onto `next`, a newer applied config. Lets unapplied work
 * survive another admin saving in between, instead of being dropped. Where
 * both touched the same setting, the draft's value wins — it is still
 * unapplied, and Review & Apply shows it before it goes out. */
export const rebaseDraft = (
  base: SignalAdminConfig,
  draft: SignalAdminDraft,
  next: SignalAdminConfig
): SignalAdminDraft => {
  const { payload } = draftPayload(base, draft);
  const out = draftFromConfig(next);
  if (payload.service_mode !== undefined)
    out.service_mode = payload.service_mode;
  if (payload.actions_surface !== undefined)
    out.actions_surface = payload.actions_surface;
  if (payload.organizations !== undefined)
    out.organizations = payload.organizations;
  Object.assign(out.layers, payload.layers ?? {});
  Object.entries(payload.rules ?? {}).forEach(([id, controls]) => {
    if (out.rules[id]) out.rules[id] = { ...out.rules[id], ...controls };
  });
  const replay = <T>(
    target: Record<string, T>,
    changes: Record<string, T | null> | undefined
  ) =>
    Object.entries(changes ?? {}).forEach(([id, value]) => {
      if (value === null) delete target[id];
      else target[id] = value;
    });
  replay(out.parameters, payload.parameters);
  replay(out.tiers, payload.tiers);
  replay(out.rule_clusters, payload.rule_clusters);
  replay(out.ctas, payload.ctas);
  replay(out.guides, payload.guides);
  replay(out.card_copy, payload.card_copy);
  replay(out.review_resolved, payload.review_resolved);
  replay(out.action_plans, payload.action_plans);
  replay(out.classifier, payload.classifier);
  replay(
    out.pipeline as Record<string, number>,
    payload.pipeline as Record<string, number | null> | undefined
  );
  // A null target means "remove this admin-authored target" — keep it.
  Object.entries(payload.targets ?? {}).forEach(([id, value]) => {
    out.targets[id] = value;
  });
  return out;
};

export const TRACE_RESULT_META: Record<
  TraceResult,
  { label: string; className: string }
> = {
  delivered: {
    label: "Delivered",
    className: "bg-emerald-50 text-emerald-700 ring-emerald-600/20",
  },
  hidden: {
    label: "Hidden by org",
    className: "bg-gray-100 text-gray-600 ring-gray-500/20",
  },
  not_delivered: {
    label: "Not delivered",
    className: "bg-amber-50 text-amber-800 ring-amber-600/20",
  },
  skipped: {
    label: "Skipped",
    className: "bg-gray-100 text-gray-600 ring-gray-500/20",
  },
  blocked: {
    label: "Blocked",
    className: "bg-red-50 text-red-700 ring-red-600/20",
  },
};

export const VALUE_LABEL: Record<string, string> = {
  active: "Active",
  inactive: "Inactive",
  enabled: "Enabled",
  disabled: "Disabled",
  hidden: "Hidden",
  on: "On",
  off: "Off",
  all: "Every organization",
  opted_in: "Only organizations turned on",
};

export const valueLabel = (value: string | number): string =>
  typeof value === "number" ? String(value) : (VALUE_LABEL[value] ?? value);

/** Mirrors effective_actions_surface() in the backend service. */
export const effectiveSurface = (
  draft: Pick<SignalAdminDraft, "service_mode" | "actions_surface">
): ActionsSurface => {
  if (draft.service_mode === "active") return draft.actions_surface;
  return draft.actions_surface === "hidden" ? "hidden" : "disabled";
};

const SURFACE_STRICTNESS: ActionsSurface[] = ["enabled", "disabled", "hidden"];

/** The more restrictive of two Open Actions values. The platform is a
 * ceiling: an organization can be stricter than it, never more open. */
export const stricterSurface = (
  a: ActionsSurface,
  b: ActionsSurface
): ActionsSurface =>
  SURFACE_STRICTNESS.indexOf(a) >= SURFACE_STRICTNESS.indexOf(b) ? a : b;

export const TIER_META: Record<
  SignalTier,
  { label: string; className: string }
> = {
  critical: { label: "Critical", className: "bg-red-50 text-red-700" },
  warning: { label: "Warning", className: "bg-amber-50 text-amber-700" },
  opp: { label: "Opportunity", className: "bg-emerald-50 text-emerald-700" },
  maint: { label: "Maintenance", className: "bg-slate-100 text-slate-600" },
};

export const TIER_ORDER: SignalTier[] = ["critical", "warning", "opp", "maint"];

/** The tier a signal fires at under this draft: the admin's choice, else
 * the ExecBridge default (which may escalate — see `dynamic_tier`). */
export const draftTier = (
  definition: Pick<SignalDefinition, "id" | "tier">,
  draft: Pick<SignalAdminDraft, "tiers"> | null | undefined
): SignalTier => draft?.tiers?.[definition.id] ?? definition.tier;

export const RUNTIME_META: Record<
  RuntimeState,
  { label: string; className: string; description: string }
> = {
  active: {
    label: "Active",
    className: "bg-emerald-50 text-emerald-700 ring-emerald-600/20",
    description: "Evaluated and shown to users.",
  },
  active_not_delivered: {
    label: "Not delivered",
    className: "bg-amber-50 text-amber-800 ring-amber-600/20",
    description: "Evaluated and recorded, but not shown to users.",
  },
  skipped: {
    label: "Evaluation off",
    className: "bg-gray-100 text-gray-600 ring-gray-500/20",
    description:
      "Not evaluated. Existing open signals are left as they are and hidden.",
  },
  blocked: {
    label: "Blocked",
    className: "bg-red-50 text-red-700 ring-red-600/20",
    description: "Cannot run.",
  },
};

export const IMPLEMENTATION_LABEL: Record<
  SignalDefinition["implementation_status"],
  string
> = {
  implemented: "Implemented",
  canonical_unimplemented: "Not implemented",
  proposed_unimplemented: "Proposed",
};

/** Human copy for rule_state() reason codes. */
export const reasonLabel = (
  reason: string,
  layerLabel: (id: string) => string
): string => {
  if (reason.startsWith("target_admin_disabled:")) {
    return `CTA target unavailable (${reason.split(":")[1]}) — not delivered`;
  }
  if (reason.startsWith("target_fallback:")) {
    return `CTA target unavailable — routed to fallback (${reason.split(":")[1]})`;
  }
  if (reason === "target_missing") return "CTA target not registered";
  if (reason.startsWith("source_admin_disabled:")) {
    const ids = reason.split(":")[1].split(",");
    return `Data layer off: ${ids.map(layerLabel).join(", ")}`;
  }
  return (
    {
      proposed_not_promoted:
        "Proposed — not promoted to the canonical registry",
      not_implemented: "Canonical but not implemented in ExecBridge",
      rule_evaluation_disabled: "Evaluation turned off",
      delivery_disabled: "User delivery turned off",
    }[reason] ?? reason
  );
};

/* ---- Run Diff (what changed between two sweeps) ---- */

export interface RunSummary {
  sweep_id: string;
  started_at: string;
  ended_at: string;
  claimed: number;
  failed: number;
  accounts_checked: number;
  accounts_changed: number;
  quiet: boolean;
}

export type RuleOutcome =
  | "match"
  | "no_match"
  | "held"
  | "covered"
  | "not_delivered"
  | "skipped"
  | "blocked";

export interface RuleMargin {
  v: number;
  th: number;
  op: "<" | "<=" | ">" | ">=";
  u?: "days" | "points" | "levels";
  time?: boolean;
  f?: string;
}

export interface RunDiffCause {
  id: string;
  kind: string;
  label: string;
  detail: Record<string, string>;
  accounts: number;
}

export interface RunDiffChange {
  field: string;
  label: string;
  before: unknown;
  after: unknown;
}

export interface RunDiffFlip {
  rule_id: string;
  title: string;
  tier: SignalTier | null;
  before: RuleOutcome;
  after: RuleOutcome;
  before_label: string;
  after_label: string;
  condition: string | null;
  explanation: string;
  margin: RuleMargin | null;
  /** "tier": the rule fires on both checks, at another tier (before/after are both "match"). */
  kind?: "tier";
  tier_before?: SignalTier;
  tier_after?: SignalTier;
}

/** "tier": the card stayed and its tier changed (tier_before → tier). */
export type CardChange = "new" | "cleared" | "covered" | "hidden" | "tier";

export interface RunDiffCard {
  change: CardChange;
  rule_id: string;
  title: string;
  tier: SignalTier | null;
  tier_before?: SignalTier;
  covered_by: string | null;
}

export interface RunDiffPersona {
  persona: string;
  users: number;
  card_delta: number;
}

export interface RunDiffAccount {
  account_id: string;
  account_name: string;
  cause_ids: string[];
  input_changes: RunDiffChange[];
  flips: RunDiffFlip[];
  cards: RunDiffCard[];
  personas: RunDiffPersona[];
}

export interface RunDiffNearMiss {
  account_id: string;
  account_name: string;
  rule_id: string;
  title: string;
  value: number;
  threshold: number;
  op: RuleMargin["op"];
  unit: RuleMargin["u"];
  time_based: boolean;
  away: number;
  away_text: string;
  fires_when: string;
}

export interface RunDiff {
  empty?: boolean;
  from: RunSummary | null;
  to: RunSummary;
  summary: {
    causes: number;
    /** Causes that ran in the window but changed nothing. */
    also_ran: string[];
    accounts_checked: number;
    accounts_changed: number;
    rule_flips: number;
    /** Signals that still fire but changed tier (absent from older servers). */
    tier_changes?: number;
    cards_new: number;
    cards_cleared: number;
    cards_covered: number;
    cards_hidden: number;
    failed_checks: number;
    personas: RunDiffPersona[];
  };
  causes: RunDiffCause[];
  threshold_changes: RunDiffChange[];
  accounts: RunDiffAccount[];
  near_misses: RunDiffNearMiss[];
  not_happening: {
    blocked: {
      rule_id: string;
      title: string;
      why: string;
      accounts: number;
    }[];
    not_delivered: { rule_id: string; title: string; accounts: number }[];
    held: { account_name: string; rule_id: string; title: string }[];
  };
  health: {
    runs: number;
    failed_checks: number;
    stuck_jobs: number;
    last_daily_recheck: string | null;
    accounts_not_checked: number;
  };
  runs: RunSummary[];
}

export interface PipelineHealth {
  status: "ok" | "warning" | "alert";
  reasons: string[];
  last_sweep_at?: string | null;
  failed_checks_24h?: number;
  stuck_jobs?: number;
  waiting_jobs?: number;
  last_daily_recheck?: string | null;
  newest_public_data?: string | null;
}
